import { ThemeProvider, useTheme } from '@mui/material/styles';
import { useMemo, useState } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { DuplicateIcon } from '@/components/icons/DuplicateIcon';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';

import {
  MergeClientProfilesModal,
  MergeProfileOption,
} from './MergeClientProfilesModal';

export function MergeClientProfilesAction({
  profiles,
  clientProfileId,
  disabled = false,
  onMerged,
}: {
  profiles: MergeProfileOption[];
  clientProfileId: string;
  disabled?: boolean;
  onMerged: () => void;
}) {
  const canEdit = useCanEditHouseholdData();
  const [isOpen, setIsOpen] = useState(false);
  const remove = profiles.find((profile) => profile.id === clientProfileId);
  const theme = useTheme();
  const elevatedModalTheme = useMemo(
    () => ({
      ...theme,
      zIndex: { ...theme.zIndex, modal: theme.zIndex.drawer + 1 },
    }),
    [theme]
  );

  if (!canEdit || !remove || profiles.length < 2) return null;

  return (
    <>
      <Tooltip title="Merge this person into another. Save or discard any changes first.">
        <IconButton
          icon={DuplicateIcon}
          variant="secondary"
          size="sm"
          ariaLabel="Merge people"
          disabled={disabled}
          onClick={() => setIsOpen(true)}
        />
      </Tooltip>
      {isOpen && (
        <ThemeProvider theme={elevatedModalTheme}>
          <MergeClientProfilesModal
            profiles={profiles}
            remove={remove}
            onClose={() => setIsOpen(false)}
            onMerged={() => {
              setIsOpen(false);
              onMerged();
            }}
          />
        </ThemeProvider>
      )}
    </>
  );
}
